import { useId, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient, type Query } from "@tanstack/react-query";
import type { ManagementRead, OperatingCurrencyAssignmentV2 } from "@voltie/localization-web";

import { useAuth } from "@/app/AuthContext";
import { useT, type Translate } from "@/app/localization";
import { errorCode, errorMessage } from "@/app/serverErrors";
import { OrgOperatingCurrencySwitch } from "@/components/OrgOperatingCurrencySwitch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { assignOrganizationOperatingCurrency, fetchOrganizationOperatingCurrency } from "@/lib/api";
import { HttpError } from "@/lib/http";

/** Query key of one account's view of one organization's operating currency. */
export const operatingCurrencyKey = (account: number | null, orgId: number) =>
  ["organization-operating-currency", account, orgId] as const;

/** One explicit assignment, carrying everything it is about: a late answer settles only its own origin. */
interface Assignment {
  readonly account: number;
  readonly orgId: number;
  readonly currency: string;
  readonly expected: string | null;
}

type Outcome =
  | { readonly kind: "assigned" | "conflict" | "mismatch" | "not_assignable" | "unavailable" | "unconfirmed"; readonly currency: string }
  | { readonly kind: "failed"; readonly error: unknown };

const originOf = (account: number | null, orgId: number) => `${account}:${orgId}`;

// An F6 unit tariff answer carries its tenant's operating currency but not the organization,
// so every cached unit tariff is marked stale; only mounted editors refetch now.
export const isUnitTariff = ({ queryKey }: Query) => queryKey.length === 3 && queryKey[0] === "unit" && queryKey[2] === "tariff";
// Outcomes after which the stored assignment may differ from what the editors last read.
const MAY_HAVE_CHANGED: ReadonlySet<Outcome["kind"]> = new Set(["assigned", "conflict", "unconfirmed"]);

// A refusal the server answered (4xx, or its own unavailable store) is final and wrote nothing;
// anything else may have committed, so it is reported as unconfirmed and never retried.
function refusal(error: unknown, currency: string): Outcome {
  const code = errorCode(error, "code");
  if (!(error instanceof HttpError)) return { kind: "unconfirmed", currency };
  if (error.status === 503 && code === "operating_currency_unavailable") return { kind: "unavailable", currency };
  if (error.status < 400 || error.status >= 500) return { kind: "unconfirmed", currency };
  switch (code) {
    case "operating_currency_conflict":
    case "operating_currency_change_not_supported":
      return { kind: "conflict", currency };
    case "stored_tariffs_mismatch":
      return { kind: "mismatch", currency };
    case "operating_currency_not_assignable":
      return { kind: "not_assignable", currency };
    case "operating_currency_unavailable":
      return { kind: "unavailable", currency };
    default:
      return { kind: "failed", error };
  }
}

function outcomeText(t: Translate, outcome: Outcome): string {
  switch (outcome.kind) {
    case "assigned": return t("operator.orgs.currency.assigned", { currency: outcome.currency });
    case "conflict": return t("operator.orgs.currency.conflict");
    case "mismatch": return t("operator.orgs.currency.tariffs_mismatch", { currency: outcome.currency });
    case "not_assignable": return t("operator.orgs.currency.not_assignable", { currency: outcome.currency });
    case "unavailable": return t("operator.orgs.currency.unavailable");
    case "unconfirmed": return t("operator.orgs.currency.unconfirmed");
    case "failed": return errorMessage(t, outcome.error, { fallback: "operator.orgs.currency.assign_failed" });
  }
}

/**
 * The organization's one operating currency (CUR-502). It projects the server's
 * answer only: candidates come from the answer, nothing is preselected or written
 * without an explicit platform-admin action, and an unreadable answer is shown as
 * unavailable, never as USD. Assignment configures the organization; it does not
 * enable payments in the currency, and the server stays the authority for it.
 * A platform admin changes an assigned currency, or an unassigned one to another than
 * the default, with new prices through OrgOperatingCurrencySwitch (CUR-207).
 */
export function OrgOperatingCurrency({ orgId }: { orgId: number }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { user, isAdmin, managerOrgIds } = useAuth();
  const account = user?.id ?? null;
  // The GET admits a platform admin or a manager of this organization (requires_org_manage);
  // a read-only admin is neither, so it gets no section. Only a platform admin assigns.
  const canView = account !== null && (isAdmin || managerOrgIds.includes(orgId));
  const canAssign = account !== null && isAdmin;
  const origin = originOf(account, orgId);
  const id = useId();
  const triggerId = `${id}-currency`;
  const helpId = `${id}-help`;

  const [drafts, setDrafts] = useState<Readonly<Record<string, string>>>({});
  const [outcomes, setOutcomes] = useState<Readonly<Record<string, Outcome>>>({});
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  // Synchronous guard: a second click before the next render sends nothing.
  const inFlight = useRef(new Set<string>());

  const q = useQuery({
    queryKey: operatingCurrencyKey(account, orgId),
    queryFn: ({ queryKey }) => fetchOrganizationOperatingCurrency(queryKey[2]),
    enabled: canView,
  });

  const settle = (a: Assignment, outcome: Outcome, confirmed?: ManagementRead<OperatingCurrencyAssignmentV2>) => {
    const from = originOf(a.account, a.orgId);
    inFlight.current.delete(from);
    setPending((p) => {
      const next = new Set(p);
      next.delete(from);
      return next;
    });
    setOutcomes((o) => ({ ...o, [from]: outcome }));
    if (confirmed) {
      setDrafts(({ [from]: _done, ...rest }) => rest);
      queryClient.setQueryData(operatingCurrencyKey(a.account, a.orgId), confirmed);
    }
    void queryClient.invalidateQueries({ queryKey: operatingCurrencyKey(a.account, a.orgId) });
    void queryClient.invalidateQueries({ queryKey: ["pricing-profiles", a.orgId] });
    if (MAY_HAVE_CHANGED.has(outcome.kind)) void queryClient.invalidateQueries({ predicate: isUnitTariff });
  };

  const assignM = useMutation({
    mutationFn: (a: Assignment) => assignOrganizationOperatingCurrency(a.orgId, a.currency, a.expected),
    retry: false,
    onSuccess: (read, a) => {
      // Only an answer about this organization that stores exactly the submitted code confirms it.
      const confirmed = read.status === "value"
        && read.value.operating.assignment === "assigned"
        && read.value.operating_currency === a.currency;
      if (confirmed) settle(a, { kind: "assigned", currency: a.currency }, read);
      else settle(a, { kind: "unconfirmed", currency: a.currency });
    },
    onError: (error, a) => settle(a, refusal(error, a.currency)),
  });

  if (!canView) return null;

  const read = q.isError ? undefined : q.data;
  const current = read?.status === "value" ? read.value : null;
  // A stored value the server cannot read is shown like an unreadable answer: no currency at all.
  const operating = current !== null && current.operating.assignment !== "unavailable" ? current.operating : null;
  const busy = pending.has(origin);
  const outcome = outcomes[origin];

  const submit = (basis: OperatingCurrencyAssignmentV2, currency: string) => {
    if (account === null || !canAssign || inFlight.current.has(origin)) return;
    if (basis.operating.assignment !== "unassigned" || !basis.assignment_choices.includes(currency)) return;
    if (!window.confirm(t("operator.orgs.currency.assign_confirm", { currency }))) return;
    inFlight.current.add(origin);
    setPending((p) => new Set(p).add(origin));
    setOutcomes(({ [origin]: _old, ...rest }) => rest);
    assignM.mutate({ account, orgId, currency, expected: basis.operating_currency });
  };

  let body: ReactNode;
  if (q.isPending && !q.isError) {
    body = (
      <div role="status">
        <Skeleton className="h-5 w-40" />
        <span className="sr-only">{t("common.state.loading")}</span>
      </div>
    );
  } else if (current === null || operating === null) {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-muted-foreground" data-testid="org-currency-unavailable">{t("operator.orgs.currency.unavailable")}</p>
        <Button size="sm" variant="outline" onClick={() => void q.refetch()}>{t("common.action.retry")}</Button>
      </div>
    );
  } else {
    const draft = drafts[origin] ?? "";
    const withdrawn = draft !== "" && !current.assignment_choices.includes(draft);
    body = (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-sm" data-testid="org-currency-value">
          {operating.assignment === "assigned"
            ? <Badge variant="outline">{operating.currency}</Badge>
            : t("operator.orgs.currency.default", { currency: operating.currency })}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="org-currency-availability">
          {operating.new_write_verdict === "eligible"
            ? t("operator.orgs.currency.new_prices_allowed", { currency: operating.currency })
            : t("operator.orgs.currency.new_prices_not_ready", { currency: operating.currency })}
        </p>
        {canAssign && operating.assignment === "unassigned" && (
          <div className="flex flex-col items-start gap-3">
            <FieldGroup>
              <Field data-invalid={withdrawn || undefined} data-disabled={busy || undefined}>
                <FieldLabel htmlFor={triggerId}>{t("operator.orgs.currency.assign_label")}</FieldLabel>
                <Select
                  value={draft}
                  onValueChange={(currency) => {
                    setDrafts((d) => ({ ...d, [origin]: currency }));
                    setOutcomes(({ [origin]: _old, ...rest }) => rest);
                  }}
                  disabled={busy}
                >
                  <SelectTrigger id={triggerId} className="w-full sm:w-56" aria-describedby={helpId} aria-invalid={withdrawn || undefined}>
                    <SelectValue placeholder={t("operator.orgs.currency.choose_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {current.assignment_choices.map((code) => (
                        <SelectItem key={code} value={code}>{code}</SelectItem>
                      ))}
                      {withdrawn && (
                        <SelectItem value={draft} disabled>{t("operator.orgs.currency.choice_withdrawn", { currency: draft })}</SelectItem>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription id={helpId}>{t("operator.orgs.currency.assign_help")}</FieldDescription>
                {withdrawn && <FieldError>{t("operator.orgs.currency.choice_withdrawn_help", { currency: draft })}</FieldError>}
              </Field>
            </FieldGroup>
            <Button size="sm" onClick={() => submit(current, draft)} disabled={busy || draft === "" || withdrawn}>
              {busy ? t("operator.orgs.currency.assigning") : t("operator.orgs.currency.assign_button")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <section aria-labelledby={`${id}-title`} className="flex flex-col gap-2" data-testid="org-operating-currency">
      <h4 id={`${id}-title`} className="font-semibold text-sm">{t("operator.orgs.currency.title")}</h4>
      {body}
      {canAssign && account !== null && (
        <OrgOperatingCurrencySwitch
          orgId={orgId}
          account={account}
          currency={operating?.currency ?? null}
          action={operating === null ? null : operating.assignment === "assigned" ? "change" : "change_unassigned"}
          disabled={busy}
        />
      )}
      {outcome?.kind === "assigned" && (
        <p role="status" className="text-sm text-muted-foreground">{outcomeText(t, outcome)}</p>
      )}
      {outcome && outcome.kind !== "assigned" && <FieldError>{outcomeText(t, outcome)}</FieldError>}
    </section>
  );
}
